import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit, ViewChild } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25Util } from "../../util/s25-util";
import { AuditService } from "../../services/audit.service";
import { OptUtil } from "../s25-opt/s25.opt.util";
import { Table } from "../s25-table/Table";
import { S25ObjectAuditAdditionalInfoComponent } from "./s25.object.audit.additional.info.component";
import { jSith } from "../../util/jquery-replacement";
import Cell = Table.Cell;
import { UserprefService } from "../../services/userpref.service";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { S25TableComponent } from "../s25-table/s25.table.component";
import { GenericTableFadePreviewComponent } from "../s25-table/generics/generic.table.fade.preview.component";
import { ReportService } from "../../services/report.service";
import { NotificationService } from "../../services/notification.service";
import { Item } from "../../pojo/Item";
import { Report } from "../../pojo/Report";
import { FlsStore } from "../../stores/fls.store";

@TypeManagerDecorator("s25-ng-object-audit-list")
@Component({
    selector: "s25-ng-object-audit-list",
    template: `
        @if (init) {
            <s25-ng-opt [modelBean]="optBean" [hasRefresh]="true" [help]="'view_eventsingle_audit'"></s25-ng-opt>
        }
        @if (init) {
            <s25-ng-table
                [caption]="'Audit Trail'"
                [unlimitedWidth]="true"
                [columnSortable]="true"
                [dataSource]="auditDataSource"
                [hasTotalRowCount]="true"
            >
                <ng-template #tableHeaderRight>
                    @if (hasReportExport()) {
                        <button class="aw-button aw-button--outline button-spacing" (click)="runReport()">
                            Export
                        </button>
                    }
                </ng-template>
            </s25-ng-table>
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25ObjectAuditListComponent implements OnInit {
    private static readonly AUDIT_EXPORT_REPORT: Report.SimpleObject = {
        rpt_id: -249,
        rpt_engine: "WS",
        isAsync: false,
    };

    readonly fls = inject(FlsStore);
    readonly exportableItemTypeIds = new Set<number>([
        Item.Ids.Event,
        Item.Ids.Organization,
        Item.Ids.Contact,
        Item.Ids.Location,
        Item.Ids.Resource,
    ]);

    @Input() itemId: number;
    @Input() itemTypeId: number;

    @ViewChild(S25TableComponent) table: S25TableComponent;

    init: boolean = false;
    dateTimeFormat: string;
    modelBean: any;
    optBean: any;
    auditDataSource: Table.Paginated;

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit() {
        this.auditDataSource = {
            type: "paginated",
            dataSource: this.getRows,
            columns: [
                {
                    id: "audit_date",
                    header: "Date",
                    sortable: true,
                },
                {
                    id: "audit_user",
                    sortable: true,
                    header: "User",
                },
                {
                    id: "audit_action",
                    sortable: true,
                    header: "Action",
                },
                {
                    id: "audit_info",
                    sortable: false,
                    header: "Additional Information",
                    cells: { component: S25ObjectAuditAdditionalInfoComponent },
                },
            ],
            defaultPageSize: 25,
            pageSizes: [25, 50, 100],
            customRowCount: this.getRowCount,
        };

        S25Util.all({
            opt: OptUtil.formAuditListBean(this.optRefresh, {
                autoInit: false,
                hasOpt: true,
                itemId: this.itemId,
                itemTypeId: this.itemTypeId,
                searchContext: "audit_" + this.itemId + "&" + this.itemTypeId,
            }),
            dateTimeFormat: UserprefService.getS25DateTimeformat(),
            fls: this.fls.ensureLoaded(),
        }).then((resp) => {
            this.optBean = resp.opt;
            this.dateTimeFormat = resp.dateTimeFormat;

            this.init = true;
            this.cd.detectChanges();
        });
    }

    optRefresh = () => {
        this.table.refresh(true, true);
    };

    hasReportExport(): boolean {
        return this.fls.hasRead("REP_LIST") && this.exportableItemTypeIds.has(S25Util.parseInt(this.itemTypeId));
    }

    getRowCount = (counts: Table.RowCounts) => {
        return `${counts.total} Matching Audit Records`;
    };

    getAuditData = (query: Table.PaginatedQuery) => {
        return AuditService.getAudits({
            itemId: this.itemId,
            itemTypeId: this.itemTypeId,
            modelBean: {
                itemsPerPage: query.pageSize,
                chosen: {
                    page: query.page + 1, // s25-table uses 0-based index by WS uses 1-based
                },
                sortCol: {
                    sort: query.sortColumn.id,
                    order: query.sortColumn.order,
                },
                cacheId: query.cacheId,
            },
        });
    };

    getRows = async (query: Table.PaginatedQuery): Promise<Table.DataSourceResponse> => {
        const listData = await this.getAuditData(query);

        const rows: Table.Row[] = [];

        if (listData?.audit?.audit_item) {
            jSith.forEach(listData.audit.audit_item, (idx: number, obj: any) => {
                const cells: Table.Row["cells"] = {};

                for (let i = 0; i < this.auditDataSource.columns.length; i++) {
                    const column = this.auditDataSource.columns[i];
                    const cell: Cell = {};

                    switch (column.id) {
                        case "audit_date":
                            cell.text = S25Datefilter.transform(obj.audit_date, this.dateTimeFormat);
                            cell.sortValue = obj.audit_date;
                            break;
                        case "audit_user":
                            cell.text = S25Util.toStr(obj.user);
                            break;
                        case "audit_action":
                            cell.text = S25Util.toStr(obj.action);
                            break;
                        case "audit_info":
                            cell.component = GenericTableFadePreviewComponent;
                            const html = S25Util.toStr(obj.context?.html_msg)
                                .replace(/href\s*=\s*["'].*?["']/g, "") // no clickable links
                                .replace(/href\s*=\s*mailto.*?>/g, ">");
                            cell.inputs = {
                                cell: {
                                    component: S25ObjectAuditAdditionalInfoComponent,
                                    inputs: {
                                        itemName: obj.context?.itemName,
                                        msg: obj.context?.msg,
                                        dateTimeStr: S25Datefilter.transform(
                                            obj.additional_information,
                                            this.dateTimeFormat,
                                        ),
                                        auditType: S25Util.parseInt(obj.audit_type),
                                        additionalInfo: S25Util.toStr(obj.additional_information),
                                        htmlMsg: html,
                                        dateFormat: this.dateTimeFormat,
                                    },
                                },
                            };
                            break;
                    }

                    cells[column.id] = cell;
                }

                rows.push({
                    id: idx,
                    name: "" + idx,
                    cells: cells,
                });
            });
        }

        return {
            rows: rows,
            totalRows: S25Util.parseInt(listData?.audit?.total_results),
            cacheId: listData?.audit?.paginate_key,
        };
    };

    runReport() {
        return ReportService.runReportRequest({
            report: S25ObjectAuditListComponent.AUDIT_EXPORT_REPORT,
            context: {
                num_parm1: this.itemId,
                num_parm2: S25Util.parseInt(this.itemTypeId).toString(),
            },
            format: null,
            delivery: null,
            otherRecipients: null,
            otherMessage: null,
            query: null,
        }).then(NotificationService.post("Report will be downloaded upon completion"), function () {});
    }
}
